// اجزای مشترک صفحات کارکنان و دسترسی IAM (AP-IAM-02): کلیدهای Query، نقش‌ها، کاتالوگ Permission، شعب، سقف‌ها،
// وضعیت عضویت، پنهان‌سازی موبایل و هشدار سقف پلن با UpgradeBadge.
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import {
  ErrorCodes,
  isApiError,
  unwrap,
  type MembershipStatus,
  type PermissionCatalogItem,
  type ReferenceItem,
  type RoleView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, UpgradeBadge, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import { SUBSCRIPTION_PATH } from '../../navigation';

/** نقش قفل مالک (BR-IAM-013: فقط از انتقال مالکیت؛ BR-IAM-014: تنها مالک). */
export const TENANT_OWNER_ROLE = 'TenantOwner';

/** کلیدهای TanStack Query ماژول IAM (به ازای مستأجر؛ جابه‌جایی مستأجر کل Cache را پاک می‌کند — AC-IAM-002.3). */
export const iamKeys = {
  all: (tenantId: string | undefined) => ['identity', tenantId, 'admin'] as const,
  memberships: (tenantId: string | undefined, filters: object) =>
    ['identity', tenantId, 'admin', 'memberships', filters] as const,
  membership: (tenantId: string | undefined, id: string) =>
    ['identity', tenantId, 'admin', 'membership', id] as const,
  profile: (tenantId: string | undefined, id: string) =>
    ['identity', tenantId, 'admin', 'membership', id, 'profile'] as const,
  impact: (tenantId: string | undefined, id: string) =>
    ['identity', tenantId, 'admin', 'membership', id, 'impact'] as const,
  provider: (tenantId: string | undefined, id: string) =>
    ['identity', tenantId, 'admin', 'provider', id] as const,

  // AP-INT-10 (US-IAM-088): شناسه‌های ارائه‌دهنده نزد بیمه‌گر.
  insurerIdentifiers: (tenantId: string | undefined, providerId: string) =>
    ['identity', tenantId, 'admin', 'provider', providerId, 'insurer-identifiers'] as const,
  roles: (tenantId: string | undefined) => ['identity', tenantId, 'roles'] as const,
  permissions: (tenantId: string | undefined) => ['identity', tenantId, 'permissions'] as const,
  branches: (tenantId: string | undefined) => ['identity', tenantId, 'branches'] as const,
  specialties: ['platform', 'reference-data', 'MEDICAL_SPECIALTIES'] as const,
  academicDegrees: ['platform', 'reference-data', 'ACADEMIC_DEGREES'] as const,
  // AP-IAM-03.
  approvalLimits: (tenantId: string | undefined) => ['identity', tenantId, 'approval-limits'] as const,
  templateDiff: (tenantId: string | undefined, roleId: string) =>
    ['identity', tenantId, 'roles', roleId, 'template-diff'] as const,
  approvals: (tenantId: string | undefined, scope: string) => ['identity', tenantId, 'approvals', scope] as const,
  myApprovalLimits: (tenantId: string | undefined) => ['identity', tenantId, 'me', 'approval-limits'] as const,
  securityEvents: (tenantId: string | undefined, filters: object) =>
    ['identity', tenantId, 'security-events', filters] as const,
  sessions: (subject: string | undefined) => ['identity', 'me', subject, 'sessions'] as const,
  mfa: (subject: string | undefined) => ['identity', 'me', subject, '2fa'] as const,
  // AP-IAM-04.
  clinicalVisibility: (tenantId: string | undefined) => ['identity', tenantId, 'clinical-visibility'] as const,
  clinicalAccess: (tenantId: string | undefined, patientId: string) =>
    ['identity', tenantId, 'clinical-access', patientId] as const,
  myOverrides: (tenantId: string | undefined) => ['identity', tenantId, 'access-overrides', 'mine'] as const,
  reviewQueue: (tenantId: string | undefined, includeReviewed: boolean) =>
    ['identity', tenantId, 'access-overrides', 'review-queue', includeReviewed] as const,
  auditEvents: (tenantId: string | undefined, filters: object) => ['identity', tenantId, 'audit-events', filters] as const,
  accessReport: (tenantId: string | undefined, patientId: string) =>
    ['identity', tenantId, 'access-report', patientId] as const,
};

export const MEMBERSHIP_STATUSES: readonly MembershipStatus[] = [
  'Active',
  'Invited',
  'InvitationExpired',
  'Suspended',
  'Revoked',
];

/** رنگ ثابت وضعیت (Design System §2.1). */
export function membershipTone(status: MembershipStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Invited':
      return 'pending';
    case 'InvitationExpired':
      return 'error';
    case 'Suspended':
      return 'no_show';
    default:
      return 'cancelled';
  }
}

/** موبایل با پنهان‌سازی میانی (۰۹۱۲***۴۵۶۷) برای فهرست کارکنان. */
export function maskMobile(mobile: string | null | undefined): string {
  if (!mobile) {
    return '';
  }
  if (mobile.length < 8) {
    return faDigits(mobile);
  }
  return faDigits(`${mobile.slice(0, 4)}***${mobile.slice(-4)}`);
}

/** عنوان نقش: نقش سفارشی ← نام تعیین‌شده مرکز (AP-IAM-03)؛ نقش سیستمی ← i18n (`iam:role.{code}`)؛ ناشناخته ← خود کد. */
export function useRoleLabel() {
  const { t } = useTranslation('iam');
  const roles = useRoles();
  const names = useMemo(
    () =>
      new Map(
        (roles.data?.items ?? [])
          .filter((role) => !role.isSystem && role.name)
          .map((role) => [role.code, role.name as string]),
      ),
    [roles.data],
  );
  return useCallback(
    (code: string) => names.get(code) ?? t(`iam:role.${code}`, { defaultValue: code }),
    [t, names],
  );
}

/** نقش‌های مستأجر با Permissionها (GET /identity/roles). */
export function useRoles() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: iamKeys.roles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/roles')),
    staleTime: 5 * 60_000,
  });
}

/** کاتالوگ Permission با Feature وابسته (GET /identity/permissions — C-AGENT-221). */
export function usePermissionCatalog() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: iamKeys.permissions(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/permissions')),
    staleTime: 5 * 60_000,
  });
}

/**
 * BR-IAM-013 در Client: نقش قابل تخصیص است اگر Permissionهای مؤثر آن (بدون Permissionهای وابسته به Feature غیرفعال، که
 * مؤثر نیستند — BR-IAM-031) زیرمجموعه Permissionهای مؤثر دعوت‌کننده باشد؛ TenantOwner هرگز. تصمیم نهایی با سرور
 * (PHX-IAM-0203).
 */
export function assignableRoles(
  roles: readonly RoleView[],
  can: (permission: string) => boolean,
  catalog: readonly PermissionCatalogItem[] | undefined,
): RoleView[] {
  const inactive = new Set((catalog ?? []).filter((item) => !item.featureEnabled).map((item) => item.code));
  return roles.filter(
    (role) =>
      role.code !== TENANT_OWNER_ROLE &&
      role.permissions.every((permission) => inactive.has(permission) || can(permission)),
  );
}

export interface BranchOption {
  id: string;
  name: string;
}

/**
 * شعب قابل تخصیص: با plt.branch.view یا manage همه شعب فعال مستأجر (PLT)؛ در غیر این صورت شعب مجاز خود کاربر (MyTenant)
 * — سرور شعب را با شعب فعال PLT می‌سنجد (C-AGENT-179).
 */
export function useBranchOptions(): { branches: BranchOption[]; isPending: boolean; error: unknown } {
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const canList = access.can('plt.branch.view') || access.can('plt.branch.manage');
  const query = useQuery({
    queryKey: iamKeys.branches(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
    enabled: canList,
    staleTime: 5 * 60_000,
  });
  const branches = useMemo<BranchOption[]>(() => {
    if (canList) {
      return (query.data?.items ?? [])
        .filter((branch) => branch.status === 'Active')
        .map((branch) => ({ id: branch.id, name: branch.name }));
    }
    return (activeTenant?.branches ?? []).map((branch) => ({ id: branch.branchId, name: branch.name ?? '' }));
  }, [canList, query.data, activeTenant]);
  return { branches, isPending: canList && query.isPending, error: canList ? query.error : null };
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface LimitUsage {
  used: number;
  /** null ← نامحدود. */
  max: number | null;
  full: boolean;
  known: boolean;
}

/** مصرف و سقف یک Limit از me/access (BR-IAM-012، BR-IAM-024). */
export function useLimitUsage(key: string): LimitUsage {
  const access = useAccess();
  const limit = access.limits.find((item) => item.key === key);
  const used = toNumber(limit?.used) ?? 0;
  const max = toNumber(limit?.max);
  return { used, max, full: max !== null && used >= max, known: limit !== undefined };
}

/** متن مصرف سقف (۲ از ۳). */
export function useLimitLabel() {
  const { t } = useTranslation('iam');
  return (usage: LimitUsage) =>
    usage.max === null
      ? t('iam:limits.usage_unlimited', { used: faDigits(usage.used) })
      : t('iam:limits.usage', { used: faDigits(usage.used), max: faDigits(usage.max) });
}

/**
 * هشدار سقف پلن (PHX-PLT-0402 — AC-IAM-011.2، AC-IAM-025.1) با UpgradeBadge و پیشنهاد ارتقا؛ برای ارائه‌دهنده پیشنهاد
 * غیرفعال‌سازی ارائه‌دهنده دیگر هم دارد.
 */
export function LimitReachedAlert({
  error,
  limitKey,
  hintKey,
}: {
  error: unknown;
  limitKey: string;
  hintKey: string;
}) {
  const { t } = useTranslation('iam');
  if (!isApiError(error) || error.code !== ErrorCodes.limitReached) {
    return null;
  }
  return (
    <Alert
      severity="warning"
      data-testid="limit-reached"
      data-error-code={error.code}
      data-limit={limitKey}
      icon={false}
      action={
        <Button component={RouterLink} to={SUBSCRIPTION_PATH} color="inherit" data-testid="limit-upgrade">
          {t('common:actions.upgrade')}
        </Button>
      }
    >
      <AlertTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t('errors:error.limit_reached')}</span>
          <UpgradeBadge feature={limitKey} to={SUBSCRIPTION_PATH} />
        </Stack>
      </AlertTitle>
      {t(hintKey)}
    </Alert>
  );
}

/** مقاطع تحصیلی از داده مرجع ACADEMIC_DEGREES (AC-IAM-083.1) — متن آزاد پذیرفته نمی‌شود. */
export function useAcademicDegrees() {
  const { tenantApi } = useTenantSession();
  const query = useQuery({
    queryKey: iamKeys.academicDegrees,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/reference-data/{dataset}', {
          params: { path: { dataset: 'ACADEMIC_DEGREES' }, query: { limit: 200 } },
        }),
      ),
    staleTime: 60 * 60_000,
  });
  const options = useMemo(
    () =>
      (query.data?.items ?? []).map((item: ReferenceItem) => {
        const data = item.data as { name_fa?: unknown; name?: unknown } | null;
        const name = data?.name_fa ?? data?.name;
        return { key: item.key, name: typeof name === 'string' ? name : item.key };
      }),
    [query.data],
  );
  return {
    options,
    isPending: query.isPending,
    error: query.error,
    isSample: (query.data?.items ?? []).some((item) => item.isSample),
  };
}

/** تخصص‌ها از داده مرجع MEDICAL_SPECIALTIES (AC-IAM-021.1). */
export function useSpecialties() {
  const { tenantApi } = useTenantSession();
  const query = useQuery({
    queryKey: iamKeys.specialties,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/reference-data/{dataset}', {
          params: { path: { dataset: 'MEDICAL_SPECIALTIES' }, query: { limit: 500 } },
        }),
      ),
    staleTime: 60 * 60_000,
  });
  const options = useMemo(
    () =>
      (query.data?.items ?? [])
        .map((item: ReferenceItem) => {
          const data = item.data as { name_fa?: unknown; name?: unknown } | null;
          const name = data?.name_fa ?? data?.name;
          return { key: item.key, name: typeof name === 'string' ? name : item.key };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'fa')),
    [query.data],
  );
  return {
    options,
    isPending: query.isPending,
    error: query.error,
    isSample: (query.data?.items ?? []).some((item) => item.isSample),
  };
}
